import React, { useMemo, useRef, useState } from "react";
import {
  Plus, Pencil, Trash2, X, Upload, FileSpreadsheet, Filter,
  CheckCircle2, ClipboardList, ScrollText, CalendarDays
} from "lucide-react";
import { Panel } from "../components/primitives";
import { useToast } from "../components/Toast";
import { useActiveTeam } from "../components/ActiveTeam";
import { useVersion } from "../components/Version";
import MatchResultEntry from "../components/MatchResultEntry";
import MatchResultModal from "../components/MatchResultModal";
import { Trophy as TrophyIcon } from "lucide-react";
import ScheduleMobile from "./ScheduleMobile";
import { api } from "../api";
import { canDo } from "../data";
import { fmtMatchTime } from "../utils/time";

const TYPES = ["League Match", "Friendly", "Tournament", "Practice"];
const STATUSES = ["Scheduled", "Completed", "Cancelled", "Postponed"];
const SPORT_FILTERS = ["All", "Cricket", "Volleyball"];

const EMPTY = {
  teamId: "", sport: "Cricket", opponent: "", date: "", time: "",
  venue: "", type: "League Match", status: "Scheduled", notes: ""
};

// ── Render helpers extracted for the Upcoming + Past sections. ──────
// Renders one <table> wrapped in team-headed sub-blocks when the
// viewer is on "All Teams"; flat single table otherwise.
function renderMatchGroups(byTeam, isAllTeams, teams, h) {
  if (isAllTeams) {
    const orderedTeamIds = [...byTeam.keys()].sort((a, b) => {
      const ta = teams.find(t => t.id === a)?.name || "";
      const tb = teams.find(t => t.id === b)?.name || "";
      return ta.localeCompare(tb);
    });
    return (
      <div className="schedule-groups">
        {orderedTeamIds.map(tid => {
          const team = teams.find(t => t.id === tid);
          const rows = byTeam.get(tid) || [];
          return (
            <div key={tid} className="schedule-group">
              <div className="schedule-group-head">
                <strong>{team?.name || "Team " + tid}</strong>
                <span>{rows.length} match{rows.length === 1 ? "" : "es"}</span>
              </div>
              <MatchTable rows={rows} teams={teams} helpers={h}/>
            </div>
          );
        })}
      </div>
    );
  }
  // Single team — flat table.
  const flat = [...byTeam.values()].flat();
  return <MatchTable rows={flat} teams={teams} helpers={h}/>;
}

function MatchTable({ rows, teams, helpers }) {
  const {
    navigate, canRecordResult, canUpdate, canDelete, today,
    setResultFor, setMatchResultFor, openEdit, remove, isPast
  } = helpers;
  return (
    <table>
      <thead>
        <tr>
          <th>Date</th><th>Time</th><th>Sport</th>
          <th>Team</th><th>Opponent</th><th>Venue</th>
          <th>Status</th><th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(m => {
          const team = teams.find(t => t.id === m.teamId);
          return (
            <tr key={m.id} className={isPast ? "schedule-row--past" : ""}>
              <td>{m.date}</td>
              <td>{m.time ? `${fmtMatchTime(m.time)} ET` : ""}</td>
              <td><span className="tag">{m.sport}</span></td>
              <td>{team?.name || "—"}</td>
              <td><b>{m.opponent}</b></td>
              <td>{m.venue}</td>
              <td>{m.status}</td>
              <td style={{display:"flex", gap:6, flexWrap:"wrap"}}>
                {navigate && !isPast && (
                  <button className="dark" onClick={() => navigate("availability")} title="Set availability"><CheckCircle2 size={14}/></button>
                )}
                {navigate && !isPast && (
                  <button className="dark" onClick={() => navigate("squad")} title="Open Squads"><ClipboardList size={14}/></button>
                )}
                {canRecordResult && m.sport === "Cricket" && m.date <= today && (
                  <button className="dark" onClick={() => setResultFor(m)} title="Record per-player stats"
                    style={{borderColor:"var(--line-gold-strong)", color:"var(--gold)"}}><ScrollText size={14}/></button>
                )}
                {canRecordResult && m.date <= today && (
                  <button className="dark" onClick={() => setMatchResultFor(m)} title="Add / edit match result"
                    style={{borderColor:"var(--line-gold-strong)", color:"var(--gold)"}}><TrophyIcon size={14}/></button>
                )}
                {canUpdate && (
                  <button
                    className="dark schedule-editBtn"
                    onClick={() => openEdit(m)}
                    title="Edit schedule (date, time, venue, opponent, status)"
                  >
                    <Pencil size={14}/> Edit
                  </button>
                )}
                {canDelete && (
                  <button
                    className="dark"
                    onClick={() => remove(m)}
                    title="Delete"
                    style={{color:"var(--red)", borderColor:"rgba(255,107,107,0.4)"}}
                  ><Trash2 size={14}/></button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export default function Schedules({ state, reload, user, navigate }) {
  const { version } = useVersion();
  const toast = useToast();
  const { activeTeam, activeTeamId, isAllTeams } = useActiveTeam();
  const [showForm, setShowForm]   = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm]           = useState(EMPTY);
  const [busy, setBusy]           = useState(false);
  const [error, setError]         = useState("");

  // Sport filter (§8.3) — applies on top of the global team scope. When a
  // specific team is active, the sport is implied so the pill row is a
  // no-op; it stays around for "All Teams" power-user view.
  const [sportFilter, setSportFilter] = useState("All");
  // Phase 4 — match-result entry modal state. Holds the match whose
  // per-player stats are being recorded.
  const [resultFor, setResultFor] = useState(null);
  // Phase 4 #1 — separate modal for the overall match result +
  // top performers (sport-agnostic; works for cricket + volleyball).
  const [matchResultFor, setMatchResultFor] = useState(null);
  const canRecordResult = canDo(user, "matchPerformance", "update");
  const today = new Date().toISOString().slice(0, 10);
  const matches = useMemo(() => {
    let pool = state.matches;
    if (!isAllTeams) pool = pool.filter(m => m.teamId === activeTeamId);
    if (sportFilter !== "All") pool = pool.filter(m => m.sport === sportFilter);
    return pool;
  }, [state.matches, sportFilter, isAllTeams, activeTeamId]);

  // Split into UPCOMING (date ≥ today, ascending) + PAST (date < today,
  // most-recent first) so the user sees what's next at the top and old
  // matches don't dominate the page.
  const upcomingMatches = useMemo(
    () => matches
      .filter(m => m.date >= today)
      .sort((a, b) => (a.date + (a.time || "")).localeCompare(b.date + (b.time || ""))),
    [matches, today]
  );
  const pastMatches = useMemo(
    () => matches
      .filter(m => m.date < today)
      .sort((a, b) => (b.date + (b.time || "")).localeCompare(a.date + (a.time || ""))),
    [matches, today]
  );

  // When viewing "All Teams", group rows under each team header so the
  // user can scan team-by-team. When a single team is active, this is
  // a no-op (one group). Returns Map<teamId, matches[]> preserving the
  // order matches were passed in.
  function groupByTeam(arr) {
    const out = new Map();
    for (const m of arr) {
      if (!out.has(m.teamId)) out.set(m.teamId, []);
      out.get(m.teamId).push(m);
    }
    return out;
  }
  const upcomingByTeam = useMemo(() => groupByTeam(upcomingMatches), [upcomingMatches]);
  const pastByTeam     = useMemo(() => groupByTeam(pastMatches),     [pastMatches]);

  // Excel upload state
  const fileRef = useRef(null);
  const [preview, setPreview]     = useState(null);
  const [uploadMode, setUploadMode] = useState("append");
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadMsg, setUploadMsg] = useState("");

  const canCreate = canDo(user, "schedule", "create");
  const canUpdate = canDo(user, "schedule", "update");
  const canDelete = canDo(user, "schedule", "delete");
  const canUpload = canDo(user, "schedule", "upload");

  function openCreate() {
    setEditingId(null);
    // Pre-fill from the active team when a single team is selected.
    const seedTeam = activeTeam || state.teams[0];
    setForm({ ...EMPTY, teamId: seedTeam?.id || "", sport: seedTeam?.sport || "Cricket" });
    setShowForm(true); setError("");
  }
  function openEdit(m) {
    setEditingId(m.id);
    setForm({
      teamId: m.teamId, sport: m.sport, opponent: m.opponent,
      date: m.date, time: m.time, venue: m.venue,
      type: m.type || "League Match", status: m.status || "Scheduled",
      notes: m.notes || ""
    });
    setShowForm(true); setError("");
  }
  function close() {
    setShowForm(false); setEditingId(null); setForm(EMPTY); setError("");
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.teamId || !form.opponent.trim() || !form.date || !form.time || !form.venue.trim()) {
      setError("Team, opponent, date, time and venue are required."); return;
    }
    setBusy(true); setError("");
    try {
      const payload = {
        teamId: Number(form.teamId), sport: form.sport,
        opponent: form.opponent.trim(), date: form.date, time: form.time,
        venue: form.venue.trim(), type: form.type, status: form.status,
        notes: form.notes.trim() || null
      };
      if (editingId) {
        await api.updateMatch(editingId, payload);
      } else {
        await api.createMatch(payload);
      }
      close();
      await reload();
    } catch (err) { setError(err.message || "Save failed."); }
    finally { setBusy(false); }
  }

  async function remove(m) {
    const teamName = state.teams.find(t => t.id === m.teamId)?.name || "Pitt Gabruz";
    if (!confirm(`Delete this match (${teamName} vs ${m.opponent} on ${m.date})?`)) return;
    try { await api.deleteMatch(m.id); await reload(); }
    catch (err) { toast.error("Delete failed: " + err.message); }
  }

  // ============== Excel upload ==============
  async function onPickFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadBusy(true); setUploadMsg(""); setPreview(null);
    try {
      const data = await api.previewSchedule(file);
      setPreview(data);
    } catch (err) {
      setUploadMsg("Preview failed: " + (err.message || err));
    } finally {
      setUploadBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function downloadTemplate() {
    setUploadMsg("");
    try { await api.downloadScheduleTemplate(); }
    catch (err) { setUploadMsg("Template download failed: " + (err.message || err)); }
  }

  async function applyUpload() {
    if (!preview) return;
    setUploadBusy(true); setUploadMsg("");
    try {
      const r = await api.applySchedule(preview.token, uploadMode);
      setUploadMsg(`${r.inserted || 0} inserted, ${r.updated || 0} updated, ${r.skipped || 0} skipped.`);
      setPreview(null);
      await reload();
    } catch (err) {
      setUploadMsg("Apply failed: " + (err.message || err));
    } finally {
      setUploadBusy(false);
    }
  }

  // Reusable form panel — extracted so the V4 mobile branch can
  // render the same create/edit form without duplicating the JSX.
  // When editing a past match, surface a clear notice so admins know
  // they're rewriting archived data (no auto-notifications fire).
  const editingPast = editingId && form.date && form.date < today;
  const formPanel = showForm && (canCreate || canUpdate) && (
    <Panel title={editingId
      ? (editingPast ? "Edit Past Match" : "Edit Match")
      : "New Match"}>
      <form className="form" onSubmit={submit}>
        {editingPast && (
          <div className="schedule-pastNotice">
            <strong>Editing a past match.</strong>
            <span>
              Players won't be re-notified. Use this to correct archived
              date, time, venue, opponent, or status records.
            </span>
          </div>
        )}
        <div className="two">
          <div>
            <label>Team *</label>
            <select
              value={form.teamId}
              onChange={e => {
                const t = state.teams.find(x => x.id === +e.target.value);
                setForm({...form, teamId: e.target.value, sport: t?.sport || form.sport});
              }}
            >
              <option value="">— select —</option>
              {state.teams.map(t => <option key={t.id} value={t.id}>{t.name} ({t.sport})</option>)}
            </select>
          </div>
          <div>
            <label>Opponent *</label>
            <input value={form.opponent} onChange={e => setForm({...form, opponent: e.target.value})} placeholder="Steel City Strikers" />
          </div>
        </div>
        <div className="two">
          <div>
            <label>Date *</label>
            <input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} />
          </div>
          <div>
            <label>Time *</label>
            <input type="time" value={form.time} onChange={e => setForm({...form, time: e.target.value})} />
          </div>
        </div>
        <label>Venue *</label>
        <input value={form.venue} onChange={e => setForm({...form, venue: e.target.value})} placeholder="Linbrook Park" />
        <div className="two">
          <div>
            <label>Match Type</label>
            <select value={form.type} onChange={e => setForm({...form, type: e.target.value})}>
              {TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label>Status</label>
            <select value={form.status} onChange={e => setForm({...form, status: e.target.value})}>
              {STATUSES.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
        </div>
        <label>Notes</label>
        <textarea rows={2} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Optional notes for this match" />
        {error && <div className="err">{error}</div>}
        <div style={{display:"flex", gap:10, justifyContent:"flex-end"}}>
          <button type="button" className="dark" onClick={close} disabled={busy}>Cancel</button>
          <button type="submit" className="btn" disabled={busy}>
            {busy ? "Saving…" : (editingId ? "Save Changes" : "Create Match")}
          </button>
        </div>
      </form>
    </Panel>
  );

  // V4 — branch into the mobile card list. Same data + form flow.
  if (version === "v4") {
    return (
      <div className="stack">
        {/* Mobile now uses its own full-screen form (not this inline formPanel).
            Kept here for the case a legacy caller still triggers openCreate/openEdit. */}
        {formPanel}
        <ScheduleMobile
          state={state}
          reload={reload}
          user={user}
          navigate={navigate}
        />
      </div>
    );
  }

  return (
    <div className="stack">
      <div className="builderTop">
        <div>
          <p>Match Schedule · Cricket · Volleyball</p>
          <h2>Schedules</h2>
        </div>
        <div style={{display:"flex", gap:8, flexWrap:"wrap"}}>
          {canUpload && (
            <>
              <input ref={fileRef} type="file" accept=".xlsx,.xls" hidden onChange={onPickFile} />
              <button className="dark" onClick={downloadTemplate} disabled={uploadBusy}>
                <FileSpreadsheet size={16}/> Template
              </button>
              <button className="btn" onClick={() => fileRef.current?.click()} disabled={uploadBusy}>
                <Upload size={16}/> Upload Excel
              </button>
            </>
          )}
          {canCreate && !showForm && (
            <button className="btn" onClick={openCreate}><Plus size={16}/> New Match</button>
          )}
          {showForm && <button className="dark" onClick={close}><X size={16}/> Close</button>}
        </div>
      </div>

      {/* ====== UPLOAD PREVIEW ====== */}
      {preview && (
        <Panel title={`Preview: ${preview.rows.length} rows from upload`}>
          <div style={{display:"flex", gap:10, marginBottom:12, alignItems:"center", flexWrap:"wrap"}}>
            <label style={{margin:0}}>Mode:</label>
            <select value={uploadMode} onChange={e => setUploadMode(e.target.value)}>
              <option value="append">Append (insert all valid rows)</option>
              <option value="upsert">Upsert (update existing by Team+Date+Opponent)</option>
              <option value="replace">Replace (soft-delete future matches first)</option>
            </select>
            <button className="btn" onClick={applyUpload} disabled={uploadBusy || !preview.rows.some(r => r.status === "valid")}>
              {uploadBusy ? "Applying…" : "Apply Upload"}
            </button>
            <button className="dark" onClick={() => setPreview(null)} disabled={uploadBusy}>Cancel</button>
          </div>
          {uploadMsg && <div className="note">{uploadMsg}</div>}
          <div style={{overflowX:"auto"}}>
          <table>
            <thead>
              <tr><th>#</th><th>Status</th><th>Team</th><th>Opponent</th><th>Date</th><th>Time</th><th>Venue</th><th>Sport</th><th>Type</th><th>Notes</th></tr>
            </thead>
            <tbody>
              {preview.rows.map((r, i) => (
                <tr key={i}>
                  <td>{r.row}</td>
                  <td><span className="tag" style={r.status === "error" ? {color:"var(--red)", borderColor:"var(--red)"} : r.status === "warning" ? {color:"var(--orange)"} : null}>
                    {r.status}
                  </span></td>
                  <td>{r.Team}</td>
                  <td>{r.Opponent}</td>
                  <td>{r.Date}</td>
                  <td>{r.Time}</td>
                  <td>{r.Venue}</td>
                  <td>{r.SportType}</td>
                  <td>{r.MatchType}</td>
                  <td style={{color: r.error ? "var(--red)" : "var(--muted)"}}>{r.error || r.warning || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </Panel>
      )}
      {!preview && uploadMsg && <div className="note">{uploadMsg}</div>}

      {/* ====== CREATE / EDIT FORM ====== */}
      {formPanel}

      {/* ====== SHARED FILTER BAR (applies to both Upcoming + Past) ====== */}
      <div className="schedule-filterBar">
        <Filter size={16} style={{color:"var(--gold)"}} />
        <span className="schedule-filterBar-lbl">Filter by sport</span>
        {SPORT_FILTERS.map(s => (
          <button
            key={s}
            type="button"
            className={sportFilter === s ? "btn" : "dark"}
            style={{padding:"6px 14px"}}
            onClick={() => setSportFilter(s)}
          >{s}</button>
        ))}
        <span style={{marginLeft:"auto", color:"var(--muted)", fontSize:12}}>
          {isAllTeams
            ? `Showing all teams · ${matches.length} match${matches.length === 1 ? "" : "es"}`
            : `${activeTeam?.name} · ${matches.length} match${matches.length === 1 ? "" : "es"}`}
        </span>
      </div>

      {/* ====== UPCOMING MATCHES — primary section, always at top ====== */}
      <Panel title={
        <span style={{display:"inline-flex", alignItems:"center", gap:8}}>
          <CalendarDays size={18}/>
          Upcoming
          <span className="schedule-sectionCount">{upcomingMatches.length}</span>
        </span>
      }>
        {upcomingMatches.length === 0 ? (
          <div className="schedule-emptySection">
            No upcoming matches{sportFilter !== "All" ? ` for ${sportFilter}` : ""}.
          </div>
        ) : (
          renderMatchGroups(upcomingByTeam, isAllTeams, state.teams, {
            navigate, canRecordResult, canUpdate, canDelete, today,
            setResultFor, setMatchResultFor, openEdit, remove
          })
        )}
      </Panel>

      {/* ====== PAST MATCHES — collapsed by default to save room ====== */}
      <Panel title={
        <span style={{display:"inline-flex", alignItems:"center", gap:8}}>
          <ScrollText size={18}/>
          Past
          <span className="schedule-sectionCount schedule-sectionCount--muted">{pastMatches.length}</span>
        </span>
      }>
        {pastMatches.length === 0 ? (
          <div className="schedule-emptySection">No past matches yet.</div>
        ) : (
          renderMatchGroups(pastByTeam, isAllTeams, state.teams, {
            navigate, canRecordResult, canUpdate, canDelete, today,
            setResultFor, setMatchResultFor, openEdit, remove,
            isPast: true
          })
        )}
      </Panel>

      {resultFor && (
        <MatchResultEntry
          match={resultFor}
          team={state.teams.find(t => t.id === resultFor.teamId)}
          squad={state.squads.find(s => s.matchId === resultFor.id)}
          members={state.members}
          user={user}
          onClose={() => setResultFor(null)}
          onSaved={() => reload && reload()}
        />
      )}

      {/* Phase 4 #1 — sport-agnostic match result + top performers. */}
      {matchResultFor && (() => {
        const t = state.teams.find(tt => tt.id === matchResultFor.teamId);
        const squad = state.squads.find(s => s.matchId === matchResultFor.id);
        // Roster: prefer the published squad players for that match;
        // fall back to the whole team roster if no squad is on file.
        const playerIds = (squad?.players || []).length > 0
          ? squad.players
          : (t?.players || []);
        const roster = playerIds
          .map(id => state.members.find(u => u.id === id))
          .filter(Boolean)
          .map(p => ({ id: p.id, name: p.name }))
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        return (
          <MatchResultModal
            match={matchResultFor}
            team={t}
            roster={roster}
            onClose={() => setMatchResultFor(null)}
            onSaved={() => reload && reload()}
          />
        );
      })()}
    </div>
  );
}
